import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { TreeNode, TreeDragDropService } from 'primeng/api';
import { ScopeHierarchyChange } from '../../models/scope-hierarchy-update-request';
import { EqmsScopeManagementService } from '../../services/eqms-scope-management.service';

export interface NodeChangeEvent {
  treeModified: boolean; // Simple flag indicating tree was modified
}

@Component({
  selector: 'app-update-tree',
  templateUrl: './update-tree.component.html',
  styleUrls: ['./update-tree.component.scss'],
  providers: [TreeDragDropService]
})
export class UpdateTreeComponent implements OnInit, OnChanges {
  @Input() treeData: TreeNode[] = [];
  @Output() onTreeChange: EventEmitter<NodeChangeEvent> = new EventEmitter<NodeChangeEvent>();

  private readonly ROOT_SCOPE_ID = 1; // ERFS root scope ID
  private originalParentLookupMap: Map<number, number | null> = new Map(); // Original state when tree loads
  private parentLookupMap: Map<number, number | null> = new Map(); // Current state

  constructor(private readonly scopeManagementService: EqmsScopeManagementService) { }

  ngOnInit(): void {
    this.initializeTree();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['treeData'] && !changes['treeData'].firstChange) {
      this.initializeTree();
    }
  }

  /**
   * Initialize tree by building parent lookup map and disabling root node dragging
   */
  private initializeTree(): void {
    // Build original parent lookup map (save initial state)
    this.originalParentLookupMap.clear();
    this.buildParentLookupMap(this.treeData, null, this.originalParentLookupMap);
    
    // Build current parent lookup map
    this.parentLookupMap.clear();
    this.buildParentLookupMap(this.treeData, null, this.parentLookupMap);
    
    // Disable dragging for root node (ERFS)
    this.disableRootNodeDragging(this.treeData);
  }

  /**
   * Recursively builds a map of scopeId -> parentScopeId for fast parent lookup
   */
  private buildParentLookupMap(nodes: TreeNode[], parentScopeId: number | null, targetMap: Map<number, number | null>): void {
    nodes.forEach(node => {
      const scopeId = this.getScopeIdFromNode(node);
      targetMap.set(scopeId, parentScopeId);
      
      if (node.children && node.children.length > 0) {
        this.buildParentLookupMap(node.children, scopeId, targetMap);
      }
    });
  }

  /**
   * Recursively disables dragging for the root scope (ERFS with ScopeId = 1)
   * Root node cannot be moved in the hierarchy
   */
  private disableRootNodeDragging(nodes: TreeNode[]): void {
    nodes.forEach(node => {
      const scopeId = this.getScopeIdFromNode(node);
      if (scopeId === this.ROOT_SCOPE_ID) {
        node.draggable = false;
        node.droppable = true; // Can still drop other nodes onto it
        node.styleClass = 'root-node-not-draggable';
      }
      if (node.children && node.children.length > 0) {
        this.disableRootNodeDragging(node.children);
      }
    });
  }



  onNodeDrop(event: any): void {
    if (!event || !event.dragNode || !event.dropNode) {
      // Clean up any stuck dragover highlights even if drop was invalid
      this.clearAllDragoverHighlights();
      return;
    }

    const draggedNode = event.dragNode;
    const draggedNodeScopeId = this.getScopeIdFromNode(draggedNode);

    // Validation: Prevent root scope from being moved
    if (draggedNodeScopeId === this.ROOT_SCOPE_ID) {
      this.clearAllDragoverHighlights();
      return;
    }

    // PrimeNG automatically moves the entire node and all its children together
    // After the drop, rebuild the current parent map from the updated tree structure
    // This ensures all nodes (including children that moved with their parent) are properly tracked
    this.parentLookupMap.clear();
    this.buildParentLookupMap(this.treeData, null, this.parentLookupMap);

    // Clean up any stuck dragover highlights
    this.clearAllDragoverHighlights();

    // Check if there are actual changes compared to original state
    const hasRealChanges = this.hasHierarchyChanges();

    // Notify parent about tree modification state
    this.onTreeChange.emit({ treeModified: hasRealChanges });
  }

  /**
   * Handler for when drag operation ends (whether dropped or cancelled)
   * Ensures cleanup happens even if drag is cancelled
   */
  onNodeDragEnd(event: any): void {
    // Clean up any stuck dragover highlights
    this.clearAllDragoverHighlights();
  }

  /**
   * Removes all dragover highlight classes that PrimeNG sometimes fails to clean up
   * This prevents nodes from staying highlighted in blue after drag operations
   */
  private clearAllDragoverHighlights(): void {
    setTimeout(() => {
      const dragoverElements = document.querySelectorAll('.p-treenode-dragover');
      dragoverElements.forEach(element => {
        element.classList.remove('p-treenode-dragover');
      });
    }, 0);
  }

  private getScopeIdFromNode(node: TreeNode): number {
    return node.data?.eQMSScopeId || node.data?.scopeId || 0;
  }

  /**
   * Check if tree has any real changes compared to original state
   * Returns true if any node's parent has changed from original
   */
  private hasHierarchyChanges(): boolean {
    let hasChanges = false;
    
    this.parentLookupMap.forEach((currentParent, scopeId) => {
      const originalParent = this.originalParentLookupMap.get(scopeId);
      if (originalParent !== currentParent) {
        hasChanges = true;
      }
    });
    
    return hasChanges;
  }

  /**
   * Expands all nodes in the tree
   */
  public expandAll(): void {
    this.scopeManagementService.recursiveExpand(this.treeData);
    //this.recursiveExpand(this.treeData);
  }

  /**
   * Collapses all nodes except the root node
   */
  public collapseAll(): void {
    this.scopeManagementService.recursiveCollapse(this.treeData, true);
   // this.recursiveCollapse(this.treeData, true);
  }

  /**
   * Recursively expands all nodes
   */
  private recursiveExpand(nodes: TreeNode[]): void {
    nodes.forEach(node => {
      node.expanded = true;
      if (node.children && node.children.length > 0) {
        this.recursiveExpand(node.children);
      }
    });
  }

  /**
   * Recursively collapses nodes
   * @param nodes Tree nodes to collapse
   * @param isRoot Whether this is the root level (root stays expanded)
   */
  private recursiveCollapse(nodes: TreeNode[], isRoot: boolean = false): void {
    nodes.forEach(node => {
      const scopeId = this.getScopeIdFromNode(node);
      
      // Keep root node expanded, collapse all others
      if (isRoot && scopeId === this.ROOT_SCOPE_ID) {
        node.expanded = true;
      } else {
        node.expanded = false;
      }
      
      if (node.children && node.children.length > 0) {
        this.recursiveCollapse(node.children, false);
      }
    });
  }

  /**
   * Calculate all hierarchy changes by comparing current state with original state
   * Called only when user confirms save
   * 
   * Note: When a parent node is moved with its children:
   * - Only the moved parent node's change is reported (its immediate parent changed)
   * - Children are not reported as separate changes (their immediate parent is still the same moved node)
   * - Backend BFS algorithm will handle updating the entire subtree's ancestors/descendants/siblings
   */
  public calculateHierarchyChanges(): ScopeHierarchyChange[] {
    const changes: ScopeHierarchyChange[] = [];

    // Compare current parent map with original
    this.parentLookupMap.forEach((currentParent, scopeId) => {
      const originalParent = this.originalParentLookupMap.get(scopeId);
      
      // If parent changed, add to changes
      if (originalParent !== currentParent) {
        changes.push({
          movedNodeScopeId: scopeId,
          oldParentScopeId: originalParent ?? null,
          newParentScopeId: currentParent ?? null
        });
      }
    });

    return changes;
  }
}
